import * as React from 'react';
import { diContainer } from 'src/dependencyInjection/diContainer';
import { DI_TYPES } from 'src/dependencyInjection/diTypes';
import { IAuthService, IAuthUser } from 'src/services/AuthService/IAuthService';
import { LoginStylePage } from './AuthPages/LoginStylePage';
import { AuthContext, ForcedScreen } from '../AuthContext';
import {
  AuthResponse,
  AuthTokenType,
} from 'src/graphql/__generated__/globalTypes';
import { match } from 'ts-pattern';
import { ShopLoginPage } from './AuthPages/ShopLoginPage';
import { gql, useApolloClient } from '@apollo/client';
import { ShopLocationContextFragmentDoc } from '../WorkOrdersPage/Contexts/__generated__/ShopLocationsContext.generated';
import { useHotToast } from 'src/utils/useHotToast';
import { useGetShopLocationsForContextQuery } from './__generated__/PageWithUserContext.generated';
import { ShopLocationContext } from '../WorkOrdersPage/Contexts/ShopLocationsContext';

export interface IPageWithUserContextProps extends React.PropsWithChildren {}

export const PageWithUserContext: React.FunctionComponent<
  IPageWithUserContextProps
> = props => {
  const authService = diContainer.get<IAuthService>(DI_TYPES.AuthService);
  const [user, setUser] = React.useState<IAuthUser | undefined>();
  const [hasLoaded, setHasLoaded] = React.useState(false);
  const [forcedScreen, setForcedScreen] = React.useState<
    ForcedScreen | undefined
  >(undefined);

  React.useEffect(() => {
    const user = authService.getCurrentUser();
    setUser(user);
    setHasLoaded(true);
  }, []);

  const apolloClient = useApolloClient();

  const authTokenType = forcedScreen ?? user?.authTokenType;
  const { shopLocations, loadingShopLocations } = useGetShopLocations(user);

  return (
    <AuthContext.Provider
      value={{
        user,
        onLogin: newUser => {
          authService.setCurrentUser(newUser);
          setUser(newUser);
        },
        logout: () => {
          authService.logout();
          setUser(undefined);
          apolloClient.resetStore();
        },
        setForcedScreen: (screen: ForcedScreen | undefined) =>
          setForcedScreen(screen),
      }}
    >
      <ShopLocationContext.Provider
        value={{
          shopLocations,
          isLoadingLocations: loadingShopLocations,
        }}
      >
        {hasLoaded &&
          match(authTokenType)
            .with(AuthTokenType.LOGIN, () => <LoginStylePage />)
            .with(AuthTokenType.ADMIN, () => props.children)
            .with(AuthTokenType.FRONT_DESK, () => props.children)
            .with(AuthTokenType.MECHANIC, () => props.children)
            .with(AuthTokenType.SHOP, () => <ShopLoginPage />)
            .otherwise(() => props.children)}
      </ShopLocationContext.Provider>
    </AuthContext.Provider>
  );
};

export function useGetShopLocations(user: AuthResponse | undefined) {
  const toast = useHotToast();
  const { loading, data } = useGetShopLocationsForContextQuery({
    onError: () => {
      toast.error('Failed to retrieve shop locations.');
    },
    skip: !user,
  });
  return {
    loadingShopLocations: loading,
    shopLocations: data?.getShopLocations || [],
  };
}

const GET_SHOP_LOCATIONS = gql`
  query GetShopLocationsForContext {
    getShopLocations {
      ...ShopLocationContext
    }
  }
  ${ShopLocationContextFragmentDoc}
`;
